import {FunctionComponent, lazy, Suspense, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {
    AppBar,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    IconButton,
    Theme,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment/moment";
import {CLOSE_VEHICLE_INFO} from "@/Redux/Disposition/Constants";
import {RootStateOrAny, useSelector} from "react-redux";
import {selectMandant, selectModules} from "@/Redux/App/AppReducer";
import {useAppDispatch, useAppSelector} from "@/Redux/Redux";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import {useHistory} from "react-router-dom";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";
import DamageButton from "@/Components/CustomComponents/DamageButton";
import {selectViewerRights, ViewerState} from "@/Redux/Auth/AuthReducer";

const BookingSearchOverlayWrapper = lazy(
    () =>
        import(
            "../../../BookingManagement/BookingSearch/Containers/BookingSearchOverlayWrapper"
            )
);
const DamagesOverviewOverlayWrapper = lazy(
    () =>
        import(
            "../../../VehicleManagement/DamagesOverview/Containers/DamagesOverviewOverlayWrapper"
            )
);

interface VehicleModalProps {
    viewData?: any;
}

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
    submit: {},
    buttonProgress: {
        position: "absolute",
        top: "54%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    wrapper: {
        margin: theme.spacing(1),
        marginTop: theme.spacing(2),
        position: "relative",
    },
    extrasIcon: {
        marginRight: 5,
        marginBottom: 8,
        display: "inline-flex",
        fontSize: 14,
    },
    appBar: {
        position: "relative",
        boxShadow: "none",
    },
    title: {
        flex: 1,
    },
}));

const VehicleInfoModal: FunctionComponent<
    VehicleModalProps
> = ({ viewData }): React.JSX.Element => {
    const {classes, cx} = useStyles();
    const modules = useSelector((state) => selectModules(state));
    const dispatch = useAppDispatch();
    const {vehicleInfoOverlayItem} = useAppSelector(
        (state: RootStateOrAny) => state.disposition
    );
    const mandant = useSelector((state) => selectMandant(state));
    const equipments = viewData?.equipmentList?.equipments;
    const [vehicleDamagesViewIsOpen, setVehicleDamagesViewIsOpen] =
        useState<boolean>(false);
    const [vehicleContractsViewIsOpen, setVehicleContractsViewIsOpen] =
        useState<boolean>(false);
    const {t} = useTranslation();
    const history = useHistory();

    const rights = useSelector((state) =>
        selectViewerRights(state as ViewerState),
    );
    const changeRight = rights.find(
        (right: any) => right.right === "KFZ_AENDERN",
    );

    let myExtras: string[] = [];

    if (vehicleInfoOverlayItem.equipment) {
        myExtras = vehicleInfoOverlayItem.equipment.split(",");

        if (myExtras[myExtras.length - 1].trim() === "") {
            myExtras.pop();
        }
    }

    const handleModalClose = () => {
        dispatch({type: CLOSE_VEHICLE_INFO});
    };

    const handleViewVehicleDamages = () => {
        setVehicleDamagesViewIsOpen(true);
    };

    const handleViewVehicleContracts = () => {
        setVehicleContractsViewIsOpen(true);
    };

    const renderVehicleContracts = () => {
        if (!vehicleContractsViewIsOpen) {
            return <></>;
        }

        return (
            <Suspense fallback={<></>}>
                <BookingSearchOverlayWrapper
                    handleClose={() => {
                        setVehicleContractsViewIsOpen(false);
                    }}
                    predefinedVehicleId={vehicleInfoOverlayItem.vehicleId}
                />
            </Suspense>
        );
    };

    const renderDamageView = () => {
        if (!vehicleDamagesViewIsOpen) {
            return <></>;
        }

        return (
            <Suspense fallback={<></>}>
                <DamagesOverviewOverlayWrapper
                    vehicleId={vehicleInfoOverlayItem.vehicleId}
                    bookingId={null}
                    booking={null}
                    hasCreateAndUpdateRights={true}
                    handleClose={() => {
                        setVehicleDamagesViewIsOpen(false);
                    }}
                />
            </Suspense>
        );
    };

    return (
        <>
            {renderDamageView()}
            {renderVehicleContracts()}
            <Dialog
                onClose={handleModalClose}
                aria-labelledby="customized-dialog-title"
                open={true}
                maxWidth={"lg"}
            >
                <AppBar className={classes.appBar}>
                    <Toolbar>
                        <Typography variant="h6" className={classes.title}>
                            {t("vehicleLine.vehicleId")}:{" "}
                            {vehicleInfoOverlayItem.vehicleId}
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={handleModalClose}
                            data-testid={"closeVehicleInfo"}
                        >
                            <CloseIcon/>
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <DialogActions style={{justifyContent: "flex-start"}}>
                    {mandant.abasId != 70447 && <>
                        {changeRight && <Tooltip
                            title={t("disposition.controls.tooltip.showVehicle")}
                        >
                            <span>
                                <Button
                                    startIcon={<VisibilityIcon/>}
                                    onClick={() => {
                                        history.push(
                                            `/manager/vehicle/${vehicleInfoOverlayItem.vehicleId}/show`
                                        );
                                    }}
                                    color="secondary"
                                    size={"small"}
                                    style={{marginLeft: -15}}
                                    variant={"outlined"}
                                >
                                    {t("general.show")}
                                </Button>
                            </span>
                        </Tooltip>
                        }

                        <Tooltip
                            title={t("disposition.controls.tooltip.showContracts")}
                        >
                        <span>
                            <Button
                                startIcon={<FindInPageIcon/>}
                                onClick={() => handleViewVehicleContracts()}
                                color="secondary"
                                size={"small"}
                                variant={"outlined"}
                            >
                                {t("vehicleLine.contracts")}
                            </Button>
                        </span>
                        </Tooltip>

                        <Tooltip
                            title={t("disposition.controls.tooltip.showDamages")}
                        >
                        <span>
                            <DamageButton
                                onClick={() => handleViewVehicleDamages()}

                            />
                        </span>
                        </Tooltip>
                    </>}
                </DialogActions>
                <DialogContent>
                    <Grid container spacing={0} style={{marginBottom: 10}}>
                        <Grid item xs={4}>
                            {t("vehicleLine.vehicleId")}:
                        </Grid>
                        <Grid item xs={8}>
                            <b>{vehicleInfoOverlayItem.vehicleId}</b>
                        </Grid>
                        <Grid item xs={4}>
                            {t("vehicleLine.licensePlate")}:
                        </Grid>
                        <Grid item xs={8}>
                            <div
                                style={{
                                    border: "1px solid black",
                                    backgroundColor: "white",
                                    color: "black",
                                    borderRadius: 3,
                                    borderLeft: "10px solid blue",
                                    width: "fit-content",
                                    paddingLeft: 3,
                                    paddingRight: 3,
                                }}
                            >
                                <b>{vehicleInfoOverlayItem?.licensePlate}</b>
                            </div>
                        </Grid>
                        <Grid item xs={4}>
                            {t("vehicleLine.vehicle")}:
                        </Grid>
                        <Grid item xs={8}>
                            <b>{vehicleInfoOverlayItem.name}</b>
                        </Grid>
                    </Grid>
                    <Grid container spacing={0} style={{marginBottom: 10}}>
                        <Grid item xs={4}>
                            {t("vehicleLine.mileage")}:
                        </Grid>
                        <Grid item xs={8}>
                            <b>{vehicleInfoOverlayItem.mileage}</b>
                        </Grid>
                        <Grid item xs={4}>
                            {t("vehicleLine.maximumMileage")}:
                        </Grid>
                        <Grid item xs={8}>
                            <b>{vehicleInfoOverlayItem.maximumMileage}</b>
                        </Grid>
                    </Grid>
                    <Grid container spacing={0} style={{marginBottom: 10}}>
                        <Grid item xs={4}>
                            {t("vehicleLine.vehicleGroup")}:
                        </Grid>
                        <Grid item xs={8}>
                            <b>
                                {
                                    vehicleInfoOverlayItem.vehicleGroup
                                        .vehicleGroupId
                                }
                            </b>
                        </Grid>
                        <Grid item xs={4}>
                            {t("vehicleLine.location")}:
                        </Grid>
                        <Grid item xs={8}>
                            <b>
                                {vehicleInfoOverlayItem.location.stationId} -{" "}
                            </b>
                        </Grid>
                    </Grid>
                    <Grid container spacing={0} style={{marginBottom: 10}}>
                        <Grid item xs={4}>
                            {t("vehicleLine.equipment")}:
                        </Grid>
                        <Grid item xs={8}>
                            <b>
                                {myExtras.map((extra: string, index: number) => (
                                    // @ts-ignore
                                    (<Tooltip
                                        key={"vehicleModalTooltip" + extra}
                                        title={
                                            equipments?.find(
                                                (equipment) =>
                                                    equipment.equipmentId ===
                                                    extra
                                            )?.description
                                        }
                                    >
                                        <>
                                            <span
                                                key={"vehicleModal" + extra}
                                                className={cx(
                                                    classes.extrasIcon,
                                                    "cx9_fzga_ico cx9-" + extra
                                                )}
                                            />
                                            {extra}
                                            {index < myExtras.length - 1 ? ", " : ""}

                                        </>
                                    </Tooltip>)
                                ))}
                            </b>
                        </Grid>
                    </Grid>
                    {modules.WIZARD && (
                        <Grid
                            container
                            spacing={0}
                            style={{marginBottom: 10}}
                        >
                            <Grid item xs={4}>
                                {t("vehicleLine.wizardState")}:
                            </Grid>
                            <Grid item xs={8}>
                                <b>{vehicleInfoOverlayItem.wizStatus}</b>
                            </Grid>
                            <Grid item xs={4}>
                                {t("vehicleLine.wizardTurnback")}:
                            </Grid>
                            <Grid item xs={8}>
                                <b>
                                    {moment
                                        .utc(
                                            vehicleInfoOverlayItem.wizTurnbackDate
                                        )
                                        .format("DD.MM.YYYY HH:mm")}
                                </b>
                            </Grid>
                        </Grid>
                    )}
                    <Grid container spacing={0} style={{marginBottom: 10}}>
                        <Grid item xs={4}>
                            {t("vehicleLine.vehicleRemarks")}:
                        </Grid>
                        <Grid item xs={8}>
                            <b>{vehicleInfoOverlayItem.remarkVehicle}</b>
                        </Grid>
                        <Grid item xs={4}>
                            {t("vehicleLine.remarks")}:
                        </Grid>
                        <Grid item xs={8}>
                            <b>{vehicleInfoOverlayItem.remark}</b>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default VehicleInfoModal;
